import { getFormById } from '@/actions';
import { FormLayout } from '@/components';
import {
  Content,
  ContentRelationshipField,
  ImageField,
} from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { FormDocument, PageDocument } from '../../../prismicio-types';
import { createClient } from '@/prismicio';
import { GoogleReCaptchaContext } from '@/context';
import { getImageAspectRadio } from '@/utils';
import { InlineFormVariation } from '@/slices/ImageWithForm/variants/InlineFormVariation';

enum SLICE_VARIATION {
  default = 'default',
  inlineForm = 'inlineForm',
}

/**
 * Props for `ImageWithForm`.
 */
export type ImageWithFormProps =
  SliceComponentProps<Content.ImageWithFormSlice> & {
    context: { caseStudyImage: ImageField };
  };

/**
 * Component for "ImageWithForm" Slices.
 */
const ImageWithForm = async ({ slice, context }: ImageWithFormProps) => {
  if (slice.variation === SLICE_VARIATION.inlineForm) {
    return <InlineFormVariation slice={slice} />;
  }

  const {
    primary: { title, short_description, form, form_alignment: formAlignment },
  } = slice;

  const typedRelationshipFieldForm = form as ContentRelationshipField &
    FormDocument;

  const client = createClient();
  const formPrismic = await client.getByID<FormDocument>(
    typedRelationshipFieldForm.id,
  );

  const {
    data: {
      form_id: formId,
      redirect_to_confirmation_page: redirect,
      confirmation_page: confirmationPage,
      utm_ebook: utmBook,
    },
  } = formPrismic;

  const typedRelationshipConfirmationPage =
    confirmationPage as ContentRelationshipField<'page'> & PageDocument;

  const hubspotFormData = await getFormById(formId);

  const settings = await client.getSingle('settings');

  const formClasses = {
    container: 'grid grid-cols-2 gap-x-4',
    button: 'h-12 min-w-[120px]',
  };

  if (hubspotFormData.fieldGroups.length > 1) {
    formClasses.container = 'flex flex-col gap-4';
    formClasses.button = 'w-full';
  }

  const getPosition = (position: string) => {
    switch (position) {
      case 'left':
        return 'lg:flex-row-reverse';
      default:
      case 'right':
        return 'lg:flex-row';
    }
  };

  const getSliceStyles = (image: ImageField) => {
    const aspectRadio = getImageAspectRadio(image);

    if (aspectRadio < 1) {
      return {
        imageSection: {
          container: 'lg:w-1/2 flex justify-center items-center',
          image: '',
        },
        contentSection: {
          container: 'lg:w-3/5',
        },
      };
    }

    return {
      imageSection: {
        container: 'w-full',
        image:
          'lg:max-w-[614px] rounded-[20px] border border-green-100 w-full h-[289px] object-cover',
      },
      contentSection: {
        container: 'w-full',
      },
    };
  };

  const styles = getSliceStyles(context.caseStudyImage ?? slice.primary.image);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-5 md:px-16 py-14"
      id="download"
    >
      <div
        className={`flex flex-col ${getPosition(formAlignment)} lg:items-center text-white gap-8`}
      >
        <div className={styles.imageSection.container}>
          <PrismicNextImage
            field={context.caseStudyImage ?? slice.primary.image}
            className={styles.imageSection.image}
          />
        </div>
        <div
          className={`flex flex-col gap-y-6 ${styles.contentSection.container}`}
        >
          <PrismicRichText field={title} />
          <PrismicRichText
            field={short_description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
          <GoogleReCaptchaContext>
            <FormLayout
              formData={hubspotFormData}
              hubspotGuidForm={formId!}
              hubspotPortalIdForm={settings.data.hubspotPortalIdForm!}
              renderTerms={false}
              redirect={redirect}
              confirmationPageUid={typedRelationshipConfirmationPage.uid}
              formContainerClass={formClasses.container}
              utmEbook={utmBook as string}
            />
          </GoogleReCaptchaContext>
        </div>
      </div>
    </section>
  );
};

export default ImageWithForm;
